import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import LiveTicker from '../components/LiveTicker';
import LiveBadge from '../components/LiveBadge';
import { Heart, Play, Clock, Trophy, Plus, X, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Available teams to follow (African football teams)
const AVAILABLE_TEAMS = [
  { name: 'Al Ahly', league: 'Egyptian Premier League', country: 'Egypt' },
  { name: 'Zamalek', league: 'Egyptian Premier League', country: 'Egypt' },
  { name: 'Mamelodi Sundowns', league: 'South African Premier Division', country: 'South Africa' },
  { name: 'Orlando Pirates', league: 'South African Premier Division', country: 'South Africa' },
  { name: 'Kaizer Chiefs', league: 'South African Premier Division', country: 'South Africa' },
  { name: 'Esperance', league: 'Tunisian Ligue 1', country: 'Tunisia' },
  { name: 'Wydad AC', league: 'Botola Pro', country: 'Morocco' },
  { name: 'Raja Casablanca', league: 'Botola Pro', country: 'Morocco' },
  { name: 'TP Mazembe', league: 'Linafoot', country: 'DR Congo' },
  { name: 'Enyimba FC', league: 'Nigeria Professional League', country: 'Nigeria' },
  { name: 'Rivers United', league: 'Nigeria Professional League', country: 'Nigeria' },
  { name: 'Simba SC', league: 'Tanzanian Premier League', country: 'Tanzania' },
  { name: 'Young Africans', league: 'Tanzanian Premier League', country: 'Tanzania' },
  { name: 'Petro de Luanda', league: 'Girabola', country: 'Angola' },
  { name: 'Nigeria', league: 'National Team', country: 'Nigeria' },
  { name: 'Egypt', league: 'National Team', country: 'Egypt' },
  { name: 'Morocco', league: 'National Team', country: 'Morocco' },
  { name: 'Senegal', league: 'National Team', country: 'Senegal' },
  { name: 'Ghana', league: 'National Team', country: 'Ghana' },
  { name: 'Cameroon', league: 'National Team', country: 'Cameroon' },
];

const Following = () => {
  const [user, setUser] = useState(null);
  const [followedTeams, setFollowedTeams] = useState([]);
  const [teamsPlayingToday, setTeamsPlayingToday] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (!token && !storedUser) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data) {
        setUser(response.data);
        loadFollowedContent(token);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/login');
    }
  };

  const loadFollowedContent = async (token) => {
    setIsLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch followed teams
      const followingRes = await axios.get(`${API}/api/user/following`, {
        headers,
        withCredentials: true
      });
      setFollowedTeams(followingRes.data.teams || []);

      // Fetch teams playing today
      const playingRes = await axios.get(`${API}/api/user/teams-playing-today`, {
        headers,
        withCredentials: true
      });
      setTeamsPlayingToday(playingRes.data.matches || []);

      // Fetch continue watching
      const watchingRes = await axios.get(`${API}/api/user/continue-watching`, {
        headers,
        withCredentials: true
      });
      setContinueWatching(watchingRes.data.items || []);

    } catch (error) {
      console.error('Failed to load followed content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const followTeam = async (teamName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/user/follow-team`, 
        { team_name: teamName },
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      setFollowedTeams(prev => [...prev, teamName]);
      toast.success(`Now following ${teamName}`);
      setShowAddTeam(false);
    } catch (error) {
      console.error('Failed to follow team:', error);
      toast.error('Failed to follow team');
    }
  };

  const unfollowTeam = async (teamName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/user/unfollow-team`, 
        { team_name: teamName },
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      setFollowedTeams(prev => prev.filter(t => t !== teamName));
      toast.success(`Unfollowed ${teamName}`);
    } catch (error) {
      console.error('Failed to unfollow team:', error);
      toast.error('Failed to unfollow team');
    }
  };

  const filteredTeams = AVAILABLE_TEAMS.filter(team => 
    !followedTeams.includes(team.name) &&
    (team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
     team.league.toLowerCase().includes(searchQuery.toLowerCase()) ||
     team.country.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <LiveTicker />
        <div className="flex items-center justify-center min-h-screen pt-28">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="following-page">
      <Navbar />
      <LiveTicker />
      
      <main className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3" data-testid="following-title">
            <Heart className="w-8 h-8 text-red-500 fill-red-500" />
            Following
          </h1>
          <p className="text-white/60 mt-2">Manage your favorite teams and never miss their matches</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Followed Teams Section */}
            <section data-testid="followed-teams-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-emerald-500" />
                  Your Teams ({followedTeams.length})
                </h2>
                <Button 
                  size="sm"
                  onClick={() => setShowAddTeam(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Team
                </Button>
              </div>
              
              {followedTeams.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {followedTeams.map((teamName) => {
                    const teamInfo = AVAILABLE_TEAMS.find(t => t.name === teamName) || { name: teamName, league: 'Team', country: '' };
                    return (
                      <div 
                        key={teamName}
                        className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between group hover:border-emerald-500/30 transition-colors"
                        data-testid={`team-card-${teamName}`}
                      >
                        <div className="flex items-center gap-4">
                          <img 
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(teamName)}&background=059669&color=fff&size=48`}
                            alt={teamName}
                            className="w-12 h-12 rounded-full"
                          />
                          <div>
                            <h3 className="text-white font-semibold">{teamName}</h3>
                            <p className="text-white/50 text-sm">{teamInfo.league}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => unfollowTeam(teamName)}
                          className="p-2 text-white/40 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          aria-label={`Unfollow ${teamName}`}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
                  <Heart className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60 mb-4">You haven't followed any teams yet</p>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => setShowAddTeam(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Follow Your First Team
                  </Button>
                </div>
              )}
            </section>

            {/* Teams Playing Today */}
            {teamsPlayingToday.length > 0 && (
              <section data-testid="teams-playing-section">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Play className="w-5 h-5 text-emerald-500" />
                  Your Teams Playing Today
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teamsPlayingToday.map((match) => (
                    <Link 
                      key={match.id}
                      to={`/match/${match.id}`}
                      className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 hover:border-emerald-500/30 transition-all group"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-emerald-500 text-xs font-semibold uppercase">
                          {match.competition_name}
                        </span>
                        {match.is_live && <LiveBadge variant="compact" />}
                      </div>
                      <h3 className="text-white font-semibold group-hover:text-emerald-400 transition-colors">
                        {match.teams || match.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Continue Watching */}
            {continueWatching.length > 0 && (
              <section data-testid="continue-watching-section">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-500" />
                  Continue Watching
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {continueWatching.map((item) => (
                    <Link 
                      key={item.id}
                      to={`/match/${item.id}`}
                      className="group"
                    >
                      <div className="relative aspect-video rounded-xl overflow-hidden mb-2">
                        <img 
                          src={item.thumbnail_url}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                          <div 
                            className="h-full bg-emerald-500"
                            style={{ width: `${item.watch_progress}%` }}
                          />
                        </div>
                      </div>
                      <h3 className="text-white font-medium truncate group-hover:text-emerald-400">
                        {item.title}
                      </h3>
                      <p className="text-white/50 text-sm">{item.watch_progress}% watched</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* Add Team Modal */}
      {showAddTeam && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowAddTeam(false)}>
          <div 
            className="bg-[#1a1a1a] rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-bold text-lg">Follow a Team</h3>
              <button onClick={() => setShowAddTeam(false)} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-10 pr-4 text-white placeholder:text-white/40 focus:outline-none focus:border-emerald-500"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[400px]">
              <div className="space-y-2">
                {filteredTeams.map((team) => (
                  <button
                    key={team.name}
                    onClick={() => followTeam(team.name)}
                    className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-white/5 transition-colors text-left"
                  >
                    <img 
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(team.name)}&background=059669&color=fff&size=40`}
                      alt={team.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div className="flex-1">
                      <p className="text-white font-medium">{team.name}</p>
                      <p className="text-white/50 text-sm">{team.league} • {team.country}</p>
                    </div>
                    <Plus className="w-5 h-5 text-emerald-500" />
                  </button>
                ))}
                {filteredTeams.length === 0 && (
                  <p className="text-white/60 text-center py-8">No teams found matching "{searchQuery}"</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Following;
